import { useEffect, useRef, useMemo } from 'react';
import { useResources } from '@/contexts/ResourceContext';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  hue: number;
}

export function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { resources } = useResources();

  // Calculate stats from resources
  const stats = useMemo(() => {
    const resourceCount = resources.length;
    // Count unique addresses as "communities"
    const uniqueAddresses = new Set(resources.map(r => r.address)).size;
    // Count connections based on shared tags
    const allTags = resources.flatMap(r => r.tags);
    const tagCounts = allTags.reduce((acc, tag) => {
      acc[tag] = (acc[tag] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const connectionCount = Object.values(tagCounts).reduce((sum, count) => sum + count - 1, 0);
    
    return {
      resources: resourceCount,
      communities: uniqueAddresses > 0 ? uniqueAddresses : 1,
      connections: Math.max(0, connectionCount),
    };
  }, [resources]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;
    const particles: Particle[] = [];
    const particleCount = 50;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight * 0.5;
    };

    const createParticle = (): Particle => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      size: Math.random() * 4 + 2,
      opacity: Math.random() * 0.5 + 0.2,
      hue: Math.random() > 0.5 ? 142 : 25, // Green or amber
    });

    const init = () => {
      resize();
      for (let i = 0; i < particleCount; i++) {
        particles.push(createParticle());
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 150) {
            ctx.beginPath();
            ctx.strokeStyle = `hsla(${particles[i].hue}, 60%, 50%, ${0.15 * (1 - dist / 150)})`;
            ctx.lineWidth = 1;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Update and draw particles
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;

        // Wrap around edges
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        // Draw particle glow
        const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 3);
        gradient.addColorStop(0, `hsla(${p.hue}, 70%, 50%, ${p.opacity})`);
        gradient.addColorStop(1, `hsla(${p.hue}, 70%, 50%, 0)`);

        ctx.beginPath();
        ctx.fillStyle = gradient;
        ctx.arc(p.x, p.y, p.size * 3, 0, Math.PI * 2);
        ctx.fill();

        // Draw core
        ctx.beginPath();
        ctx.fillStyle = `hsla(${p.hue}, 80%, 60%, ${p.opacity + 0.3})`;
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      });

      animationId = requestAnimationFrame(animate);
    };

    init();
    animate();

    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <section className="relative overflow-hidden isolate">
      {/* Animated background gradient */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10 animate-pulse" />
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            background: 'radial-gradient(ellipse at 30% 50%, hsl(var(--primary) / 0.15) 0%, transparent 50%), radial-gradient(ellipse at 70% 50%, hsl(var(--accent) / 0.1) 0%, transparent 50%)',
          }}
        />
      </div>

      {/* Particle canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none opacity-60"
      />

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 py-24 md:py-32 text-center">
        <div className="space-y-8 stagger-children">
          {/* Logo */}
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 rounded-full blur-xl animate-pulse" />
              <div className="relative w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-4 border-background shadow-xl">
                <img
                  src="https://blossom.primal.net/88b784fe3b73f35b2f8db64a798c06d79fa1e40c1e77748d4d15b8ef55fb63b7.png"
                  alt="Regenerate Community Kāeo Whangaroa Mutual Aid Logo"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          <div className="space-y-3 sm:space-y-4">
            <h1 className="text-3xl md:text-6xl font-bold tracking-tight">
              <span className="gradient-text">Regenerate Community</span>
              <br />
              <span className="text-foreground">Kāeo Whangaroa Mutual Aid</span>
            </h1>
            <p className="text-base md:text-2xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              A living map of mutual aid, where neighbours grow stronger together.
              <span className="hidden sm:block mt-2">
                Find and share resources that nurture our collective resilience.
              </span>
            </p>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-6 md:gap-12 pt-4">
            {[
              { value: stats.resources, label: 'Resources' },
              { value: stats.communities, label: 'Communities' },
              { value: stats.connections, label: 'Connections' },
            ].map((stat, i) => (
              <div key={i} className="text-center group">
                <div className="text-2xl md:text-4xl font-bold gradient-text">{stat.value}</div>
                <div className="text-xs md:text-sm text-muted-foreground mt-1 group-hover:text-foreground transition-colors">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          {/* CTA hints */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 pt-4 sm:pt-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-secondary/80 backdrop-blur-sm border border-border text-xs sm:text-sm">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-secondary-foreground">Share resources</span>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-secondary/80 backdrop-blur-sm border border-border text-xs sm:text-sm">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
              <span className="text-secondary-foreground">Connect</span>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-secondary/80 backdrop-blur-sm border border-border text-xs sm:text-sm">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-secondary-foreground">Grow network</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
}
